import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import logoSvg from "../resources/logo.svg";
import { CookieConsent } from "./components/CookieConsent";
import { AnimatedHeaderTitle } from "./components/AnimatedHeaderTitle";
import { FavoriteHeart } from "./components/ui/FavoriteHeart";
import { BellIcon } from "./components/icons";
import { NotificationModal } from "./components/NotificationModal";
import { useApmRouteTracker } from "./lib/useApmRouteTracker";
import { getEventMetadata } from "./lib/useEventMetadata";
import { getClosestDayId } from "./lib/useFestivalDay";
import { getAllDays } from "./services/dataService";

const App: React.FC = () => {
  // Track route changes as OTel spans in Kibana APM.
  useApmRouteTracker();

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isPwa, setIsPwa] = useState(false);
  const [copied, setCopied] = useState(false);

  const [hasUnreadNotification, setHasUnreadNotification] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationCreatedAt, setNotificationCreatedAt] = useState("");

  const checkNotifications = async () => {
    try {
      const storedEtag = localStorage.getItem("notification_etag");
      const headers: Record<string, string> = {};
      if (storedEtag) {
        headers["If-None-Match"] = storedEtag;
      }
      const res = await fetch("/api/notification", { headers });
      if (res.status === 200) {
        const data = await res.json();
        const message = data.message || "";
        const title = data.title || "";
        const createdAt = data.createdAt || "";
        const validUntil = data.validUntil || "";

        localStorage.setItem("notification_message", message);
        localStorage.setItem("notification_title", title);
        localStorage.setItem("notification_created_at", createdAt);
        localStorage.setItem("notification_valid_until", validUntil);
        const etag = res.headers.get("ETag");
        if (etag) {
          localStorage.setItem("notification_etag", etag);
        }

        const now = new Date();
        const isExpired = validUntil && now > new Date(validUntil);
        const isValid = message && !isExpired;

        const lastReadEtag = localStorage.getItem("notification_last_read_etag") || "";
        if (isValid && etag !== lastReadEtag) {
          setHasUnreadNotification(true);
        } else {
          setHasUnreadNotification(false);
        }
        setNotificationMessage(message);
        setNotificationTitle(title);
        setNotificationCreatedAt(createdAt);
      } else if (res.status === 304) {
        const message = localStorage.getItem("notification_message") || "";
        const title = localStorage.getItem("notification_title") || "";
        const createdAt = localStorage.getItem("notification_created_at") || "";
        const validUntil = localStorage.getItem("notification_valid_until") || "";

        const now = new Date();
        const isExpired = validUntil && now > new Date(validUntil);
        const isValid = message && !isExpired;

        setNotificationMessage(message);
        setNotificationTitle(title);
        setNotificationCreatedAt(createdAt);

        const etag = localStorage.getItem("notification_etag") || "";
        const lastReadEtag = localStorage.getItem("notification_last_read_etag") || "";
        if (isValid && etag !== lastReadEtag) {
          setHasUnreadNotification(true);
        } else {
          setHasUnreadNotification(false);
        }
      }
    } catch (err) {
      console.error("Failed to fetch notification", err);
    }
  };

  useEffect(() => {
    const msg = localStorage.getItem("notification_message") || "";
    const title = localStorage.getItem("notification_title") || "";
    const createdAt = localStorage.getItem("notification_created_at") || "";
    const validUntil = localStorage.getItem("notification_valid_until") || "";

    const now = new Date();
    const isExpired = validUntil && now > new Date(validUntil);
    const isValid = msg && !isExpired;

    setNotificationMessage(msg);
    setNotificationTitle(title);
    setNotificationCreatedAt(createdAt);

    const etag = localStorage.getItem("notification_etag") || "";
    const lastReadEtag = localStorage.getItem("notification_last_read_etag") || "";
    if (isValid && etag !== lastReadEtag) {
      setHasUnreadNotification(true);
    }

    let intervalId: any = null;
    const startTimeout = setTimeout(() => {
      checkNotifications();
      intervalId = setInterval(checkNotifications, 3 * 60 * 1000);
    }, 10000);

    return () => {
      clearTimeout(startTimeout);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  const handleNotificationClick = () => {
    const validUntil = localStorage.getItem("notification_valid_until") || "";
    const now = new Date();
    const isExpired = validUntil && now > new Date(validUntil);

    if (isExpired) {
      setNotificationMessage("");
      setNotificationTitle("");
      setNotificationCreatedAt("");
    }

    const etag = localStorage.getItem("notification_etag") || "";
    if (etag) {
      localStorage.setItem("notification_last_read_etag", etag);
    }
    setHasUnreadNotification(false);
    setIsNotificationModalOpen(true);
  };

  useEffect(() => {
    const checkStandalone = () => {
      const isStandalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        (navigator as any).standalone === true ||
        document.referrer.includes("android-app://");
      setIsPwa(isStandalone);
    };
    checkStandalone();
  }, []);

  const handleShareClick = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy URL:", err);
    }
  };

  // Scroll to top on page switches, unless it's a scroll to a timeline stage section
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const stage = searchParams.get("stage");
    const hasStageScroll = location.pathname === "/" && stage && stage !== "all";

    if (!hasStageScroll) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  const isFavoritesActive = location.pathname === "/" && searchParams.get("favorites") === "true";
  const isProgramActive = location.pathname === "/" && searchParams.get("favorites") !== "true";

  const handleProgrammeClick = (e: React.MouseEvent) => {
    if (location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("favorites", "false");
          return next;
        },
        { replace: true },
      );
    }
  };

  const handleFavoritesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === "/") {
      const isFav = searchParams.get("favorites") === "true";
      window.scrollTo({ top: 0, behavior: "smooth" });
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const nextFav = !isFav;
          next.set("favorites", String(nextFav));
          if (nextFav) {
            next.set("stage", "all");
          }
          return next;
        },
        { replace: true },
      );
    } else {
      const days = getAllDays();
      const relevantDay = getClosestDayId(days);
      navigate(`/?day=${relevantDay}&stage=all&favorites=true`);
    }
  };

  const { links } = getEventMetadata();
  const supportLink = links.donate;
  const ticketsLink = links.tickets;

  // Admin routes no longer render inside App, so the support tape is always shown

  return (
    <div className="absolute left-0 right-0 min-h-screen w-[95%] sm:max-w-[440px] md:max-w-[480px] lg:max-w-[540px] mx-auto bg-background text-text pb-24 border-x border-border shadow-2xl">
      {/* Offline Riso Grain overlay */}
      <div className="grain" style={{ overflow: "hidden" }} aria-hidden="true" />

      {/* Grainy Brand Gradient Background */}
      <div className="grainy-bg-container" aria-hidden="true" />

      {/* ── Sticky App Header ── */}
      <header
        className="sticky top-[5px] z-50 backdrop-blur-md border-b border-white/5 px-6 py-3"
        style={{ backgroundColor: "rgba(22, 22, 22, 0.75)" }}
      >
        <div className="flex items-center justify-between">
          <NavLink
            to="/"
            className="flex items-center gap-2.5"
            aria-label="Főoldal és teljes műsorrend"
          >
            <div className="w-[42px] h-[42px] rounded-full bg-black border-[2.5px] border-[#F7C4FF] shadow-[0_0_10px_rgba(247,196,255,0.3)] flex items-center justify-center relative p-1 z-10">
              <img
                src={logoSvg}
                alt="Tilos Maraton Logo"
                className="w-full h-full object-contain relative z-10"
              />
              {/* Concentric Skewed Halos / Orbitals (All 3 variants) */}
              <span
                className="absolute inset-[-3.5px] border-[1.5px] border-[#22D3BA]/70 rounded-full pointer-events-none z-0 spin-slow"
                style={
                  {
                    "--skew-x": "-12deg",
                    "--scale": Math.random() / 10 + 0.9,
                  } as React.CSSProperties
                }
              />
              <span
                className="absolute inset-[0.5px] border-[2px] border-[#F7C4FF]/70 rounded-full pointer-events-none z-0 spin-mid"
                style={
                  { "--skew-x": "8deg", "--scale": Math.random() / 9 + 0.9 } as React.CSSProperties
                }
              />
              <span
                className="absolute inset-[-5.5px] border-[1.2px] border-[#C0B5FC]/60 rounded-full pointer-events-none z-0 spin-fast"
                style={
                  { "--skew-x": "14deg", "--scale": Math.random() / 8 + 0.9 } as React.CSSProperties
                }
              />
            </div>
            <div className="flex flex-col">
              <AnimatedHeaderTitle />
            </div>
          </NavLink>

          <div className="flex items-center gap-2 relative">
            <button
              type="button"
              onClick={handleNotificationClick}
              className="hbtn flex items-center justify-center p-2 rounded-full transition-all duration-300 hover:bg-[#22d3ba]/10 text-[#9f98b5] hover:text-[#22d3ba] relative cursor-pointer"
              aria-label="Értesítések"
            >
              <BellIcon
                filled={hasUnreadNotification}
                className={`transition-all duration-300 ${
                  hasUnreadNotification
                    ? "text-[#22d3ba] scale-110 drop-shadow-[0_0_6px_rgba(34,211,186,0.5)]"
                    : "text-current opacity-70 hover:opacity-100"
                } h-5 w-5`}
              />
              {hasUnreadNotification && (
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-[#22d3ba] rounded-full border border-background animate-pulse" />
              )}
            </button>

            <FavoriteHeart
              active={isFavoritesActive}
              onClick={handleFavoritesClick}
              className="hbtn flex items-center justify-center p-2 rounded-full"
              iconClassName="h-5.5 w-5.5"
              ariaLabel={
                isFavoritesActive ? "Összes fellépés mutatása" : "Csak a kedvencek mutatása"
              }
            />
          </div>
        </div>
      </header>

      {/* ── Masking Tape Support Banner ── */}
      {
        <section className="mt-6 mb-4 px-3.5 py-2 masking-tape-gradient text-[#0F0F0F] font-['Unbounded'] text-[12px] font-bold tracking-tight rounded-[2px] shadow-[3px_3px_0px_rgba(0,0,0,0.8),_inset_-1px_-1px_0px_rgba(255,255,255,0.4)] rotate-[-1deg] flex items-center justify-between gap-2.5 relative z-10">
          <div className="absolute left-[-6px] top-0 bottom-0 w-1.5 bg-inherit [clip-path:polygon(100%_0,_0_25%,_100%_50%,_0_75%,_100%_100%)]" />
          <div className="absolute right-[-6px] top-0 bottom-0 w-1.5 bg-inherit [clip-path:polygon(0_0,_100%_25%,_0_50%,_100%_75%,_0_100%)]" />

          <button
            type="button"
            role="button"
            onClick={() => window.open(supportLink.url, "_blank")}
            className="text-[9.5px] font-extrabold uppercase tracking-wider px-2 py-1.5 border border-[#0F0F0F] rounded bg-transparent hover:bg-[#0F0F0F] hover:text-white transition-all cursor-pointer flex-[2] text-center"
          >
            {supportLink.name}
          </button>

          <button
            type="button"
            role="button"
            onClick={() => window.open(ticketsLink.url, "_blank")}
            className="text-[9.5px] font-extrabold uppercase tracking-wider px-3.5 py-1.5 border border-[#0F0F0F] rounded bg-transparent hover:bg-[#0F0F0F] hover:text-white transition-all cursor-pointer flex-[1] text-center"
          >
            {ticketsLink.name}
          </button>
        </section>
      }

      {/* ── Main Router Content ── */}
      <main className="px-4 py-2 relative z-10">
        <Outlet />
      </main>

      {/* ── Sticky Bottom Navigation Bar ── */}
      <nav
        className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full sm:max-w-[440px] md:max-w-[480px] lg:max-w-[540px] backdrop-blur-lg border-t border-white/5 flex py-2 px-1 pb-4 z-50"
        style={{ backgroundColor: "rgba(22, 22, 22, 0.75)" }}
        aria-label="Fő navigáció"
      >
        <NavLink
          to="/"
          onClick={handleProgrammeClick}
          className={`nitem ${isProgramActive ? "active" : ""}`}
          aria-label="Műsorrend és programok megnyitása"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="pointer-events-none"
          >
            <title>Műsor ikon</title>
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          Műsor
        </NavLink>

        <NavLink
          to="/search"
          className={({ isActive }) => `nitem ${isActive ? "active" : ""}`}
          aria-label="Kereső oldal megnyitása"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="pointer-events-none"
          >
            <title>Keresés ikon</title>
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          Keresés
        </NavLink>

        <NavLink
          to="/info"
          className={({ isActive }) => `nitem ${isActive ? "active" : ""}`}
          aria-label="Fesztivál információk és térkép megnyitása"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="pointer-events-none"
          >
            <title>Infó ikon</title>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          Infó
        </NavLink>

        {isPwa && (
          <button
            type="button"
            onClick={handleShareClick}
            className={`nitem transition-all ${copied ? "!text-main" : ""}`}
            aria-label="Link megosztása"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke={copied ? "#22D3BA" : "currentColor"}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <title>{copied ? "Sikeres másolás" : "Megosztás ikon"}</title>
              {copied ? (
                <polyline points="20 6 9 17 4 12" />
              ) : (
                <>
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                  <polyline points="16 6 12 2 8 6" />
                  <line x1="12" y1="2" x2="12" y2="15" />
                </>
              )}
            </svg>
            {copied ? "Másolva!" : "Megosztás"}
          </button>
        )}
      </nav>
      <NotificationModal
        isOpen={isNotificationModalOpen}
        onClose={() => setIsNotificationModalOpen(false)}
        title={notificationTitle}
        message={notificationMessage}
        createdAt={notificationCreatedAt}
      />
      <CookieConsent />
    </div>
  );
};

export default App;
